import type Player from 'video.js/dist/types/player';
import { videoIntervalTracker } from './video-interval-tracker';

import {
  sendRemoteEvent,
  RemoteEventType,
  toNumberWithDefaultZero,
} from '~/services/continue-watching/client.ts';

const PLUGIN_IDENTIFIER = 'continue-watching';
const MIN_SEEK_DURATION = 3;

let currentTime = 0;
let resumeTime = 0;
let seekTime = 0;
let progress = 0;
let secondsFromStart = '';
let sessionStartAt = 0;
let extraCWData: any = {};
let extraProgressData: any = {};
let isStarted = false;
let isPaused = false;
let isCompleted = false;
let heartbeatInterval: any;

const setDefaultValues = () => {
  currentTime = 0;
  resumeTime = 0;
  seekTime = 0;
  progress = 0;
  secondsFromStart = '0';
  sessionStartAt = 0;
};

const getProgress = (time: number, duration: number): number => {
  return duration > 0 ? time / duration : 0;
};

function continueWatchingPlugin(
  this: Player,
  options: {
    entryId: string;
    resumeTime: string;
    duration: number;
    entry: any;
    isContinueWatchingSyncedFeed: boolean;
    continueWatchingPluginConfiguration: any;
  }
): void {
  if (!options?.isContinueWatchingSyncedFeed) return;

  resumeTime = toNumberWithDefaultZero(options.resumeTime);
  sessionStartAt = toNumberWithDefaultZero(options.resumeTime);
  progress = getProgress(resumeTime, options.duration);

  const customConfiguration = options?.continueWatchingPluginConfiguration;

  const {
    minimum_auto_resume_content_duration,
    auto_resume_completion_threshold,
    update_frequency,
    send_heartbeat_on_pause,
    pause_event_heartbeat_interval,
    enable_video_interval,
    video_interval,
    enable_hearbeat,
  } = customConfiguration || {};

  const isUpdateFrequencyReached = (time: number): boolean => {
    return (
      Math.abs(Number(time) - (resumeTime || 0)) >= Number(update_frequency)
    );
  };

  const isSeeking = (time: number): boolean => {
    if ((currentTime || currentTime === 0) && time !== resumeTime) {
      return Math.abs(Number(time) - currentTime) > MIN_SEEK_DURATION;
    }

    return false;
  };

  const isVideoFinished = (time: number): boolean => {
    return time - auto_resume_completion_threshold >= options.duration;
  };

  async function onTimeUpdate(this: any) {
    const time = this.currentTime();
    const duration = this.duration();
    const seekStatus = isSeeking(time);

    if (
      !duration ||
      duration < minimum_auto_resume_content_duration ||
      (!time && time !== 0) ||
      (isPaused && !seekStatus)
    )
      return;

    // showPlayNextOverlay(playableItem);

    progress = getProgress(time, duration);
    secondsFromStart = String(time);

    if (isCompleted && !isVideoFinished(time)) {
      // case when playback is rewinded after completion
      isCompleted = false;
    }

    const updateStatus = isUpdateFrequencyReached(time);

    if (seekStatus) {
      const seek = time - currentTime;
      seekTime += seek;
    } else {
      if (isVideoFinished(time)) {
        resumeTime = time;
        onComplete();
      } else if (updateStatus) {
        resumeTime = time;
      }
    }

    if (updateStatus && enable_hearbeat && heartbeatInterval) {
      sendEvent(RemoteEventType.Progress, {
        currentProgress: progress,
      });
    }

    currentTime = time;
  }

  function onLoadedMetadata(this: any) {
    this.currentTime(resumeTime);
  }

  function sendEvent(eventType: string, eventOptions: any = {}) {
    if (!options.isContinueWatchingSyncedFeed) return;

    const { currentProgress, ...remoteEventOptions } = eventOptions;
    const progressValue = toNumberWithDefaultZero(currentProgress || progress);

    sendRemoteEvent({
      item: options.entry,
      eventType: eventType as RemoteEventType,
      videoId: options.entryId,
      progress: progressValue,
      secondsFromStart:
        secondsFromStart || String(progressValue * options.duration),
      sessionStartAt: sessionStartAt + toNumberWithDefaultZero(seekTime),
      extraCWData: {
        ...extraCWData,
        ...extraProgressData,
        ...remoteEventOptions,
      },
    });
  }

  function startHeartbeatOnPause() {
    if (!isPaused && !send_heartbeat_on_pause && !enable_hearbeat) return;

    heartbeatInterval = setInterval(() => {
      sendEvent(RemoteEventType.Paused);
    }, pause_event_heartbeat_interval * 1000);
  }

  function stopHeartbeatOnPause() {
    clearInterval(heartbeatInterval);
  }

  function onPlay() {
    extraCWData = {
      ...options.entry.extensions?.[PLUGIN_IDENTIFIER],
      ...extraCWData,
    };

    if (isPaused) {
      stopHeartbeatOnPause();
      isPaused = false;
    }

    if (!isStarted) {
      sendEvent(RemoteEventType.Started);
      isStarted = true;
    }
  }

  function onPause(this: any) {
    if (!isPaused) {
      resumeTime = this.currentTime();
      sendEvent(RemoteEventType.Progress);

      isPaused = true;
      startHeartbeatOnPause();
    }
  }

  function onClose(this: any) {
    if (isPaused) {
      stopHeartbeatOnPause();
    }

    resumeTime = this.currentTime();
    sendEvent(RemoteEventType.Stopped);

    removeListeners();
    setDefaultValues();
    isStarted = false;
  }

  function onComplete() {
    if (isCompleted) {
      return;
    }

    isCompleted = true;
    isStarted = false;

    // TODO: remove item from CW list
  }

  const removeListeners = () => {
    this.off('dispose', onClose);
    this.off('pause', onPause);
    this.off('play', onPlay);
    this.off('loadedmetadata', onLoadedMetadata);
    this.off('timeupdate', onTimeUpdate);
  };

  const addListeners = () => {
    this.on('dispose', onClose);
    this.on('pause', onPause);
    this.on('play', onPlay);
    this.on('loadedmetadata', onLoadedMetadata);
    this.on('timeupdate', onTimeUpdate);
  };

  addListeners();

  if (enable_video_interval) {
    videoIntervalTracker(this, video_interval, sendEvent);
  }
}

export default continueWatchingPlugin;
